class WebGLPageTransition {
    constructor() {
        this.container = document.getElementById('bonj-page-transition');
        this.canvas = document.getElementById('bonj-transition-canvas');
        if (!this.container || !this.canvas) return;

        this.textureUrl = this.container.getAttribute('data-texture');
        const desktopLogo = this.container.getAttribute('data-logo');
        const mobileLogo = this.container.getAttribute('data-logo-mobile');
        // Mobile image (theme settings) on screens < 750px, else desktop image
        this.logoUrl = (mobileLogo && window.matchMedia('(max-width: 749px)').matches) ? mobileLogo : desktopLogo;
        if (!this.textureUrl || !this.logoUrl) return;

        this.gl = this.canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false });
        if (!this.gl) return; // Fallback gracefully if WebGL is not supported

        this.progress = 0;
        this.isAnimating = false;
        this.animationDuration = 1000; // ms
        this.animationStartTime = 0;
        this.animationDirection = 1; // 1 for leave (cover screen), -1 for enter (reveal screen)

        this.injectCSS();
        this.setupWebGL();
        this.bindEvents();

        // Only play the reveal ("rip open") animation when this page load
        // actually followed an internal link click (playLeaveAnimation sets
        // this flag right before navigating). A plain reload, direct visit,
        // typed URL, or back/forward navigation should not trigger it.
        let cameFromTransition = false;
        try {
            cameFromTransition = sessionStorage.getItem('bonjTransitionActive') === '1';
            sessionStorage.removeItem('bonjTransitionActive');
        } catch (e) {}

        if (cameFromTransition) {
            this.playEnterAnimation();
        } else {
            this.container.classList.remove('is-active');
        }

        // Drop the synchronous CSS pre-cover (injected in <head> before this
        // script/WebGL was ready) now that the canvas has taken over — or,
        // on a plain reload, because it was never needed.
        document.documentElement.classList.remove('bonj-transition-cover');
    }

    injectCSS() {
        if (document.getElementById('bonj-transition-styles')) return;
        
        const style = document.createElement('style');
        style.id = 'bonj-transition-styles';
        style.textContent = `
            #bonj-page-transition {
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                pointer-events: none;
                z-index: 2147483647;
                overflow: hidden;
            }
            #bonj-page-transition.is-active {
                pointer-events: all;
            }
            #bonj-transition-canvas {
                width: 100%;
                height: 100%;
                display: block;
            }
        `;
        document.head.appendChild(style);
    }

    setupWebGL() {
        const gl = this.gl;

        const vertexShaderSource = `
            attribute vec2 position;
            attribute vec2 uv;
            varying vec2 vUv;
            varying vec2 vImageUv;
            uniform vec2 uRatio;

            float map(float value, float min1, float max1, float min2, float max2) {
                return min2 + (value - min1) * (max2 - min2) / (max1 - min1);
            }

            void main() {
                vUv = uv;
                vImageUv = vec2(
                    map(uv.x, 0.0, 1.0, 0.5 - uRatio.x / 2.0, 0.5 + uRatio.x / 2.0),
                    map(uv.y, 0.0, 1.0, 0.5 - uRatio.y / 2.0, 0.5 + uRatio.y / 2.0)
                );
                gl_Position = vec4(position, 0.0, 1.0);
            }
        `;

        const fragmentShaderSource = `
            #define PI 3.1415926538
            #define NUM_OCTAVES 5

            precision highp float;
            uniform float uHorizontal;
            uniform float uProgress;
            uniform float uMaxAmplitude;
            uniform float uRippedNoiseFrequency;
            uniform float uCurveNoiseFrequency;
            uniform float uRippedNoiseAmplitude;
            uniform float uCurveNoiseAmplitude;
            uniform float uAspect;
            uniform float uRippedDelta;
            uniform sampler2D uTexture;
            uniform sampler2D uLogo;
            uniform vec2 uLogoScale;
            uniform float uRippedHeight;
            uniform vec3 uColor;
            uniform sampler2D uImage;
            uniform vec3 uBackground;
            uniform float uBackgroundOpacity;
            uniform bool uInverted;
            
            varying vec2 vUv;
            varying vec2 vImageUv;

            vec3 permute(vec3 x) { return mod(((x*34.0)+1.0)*x, 289.0); }
            
            float snoise(vec2 v){
              const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
              vec2 i  = floor(v + dot(v, C.yy) );
              vec2 x0 = v -   i + dot(i, C.xx);
              vec2 i1;
              i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
              vec4 x12 = x0.xyxy + C.xxzz;
              x12.xy -= i1;
              i = mod(i, 289.0);
              vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 )) + i.x + vec3(0.0, i1.x, 1.0 ));
              vec3 m = max(0.5 - vec3(dot(x0,x0), dot(x12.xy,x12.xy), dot(x12.zw,x12.zw)), 0.0);
              m = m*m ;
              m = m*m ;
              vec3 x = 2.0 * fract(p * C.www) - 1.0;
              vec3 h = abs(x) - 0.5;
              vec3 ox = floor(x + 0.5);
              vec3 a0 = x - ox;
              m *= 1.79284291400159 - 0.85373472095314 * ( a0*a0 + h*h );
              vec3 g;
              g.x  = a0.x  * x0.x  + h.x  * x0.y;
              g.yz = a0.yz * x12.xz + h.yz * x12.yw;
              return 130.0 * dot(m, g);
            }

            float fbm(vec2 x) {
              float v = 0.0;
              float a = 0.5;
              vec2 shift = vec2(100.0);
              mat2 rot = mat2(cos(0.5), sin(0.5), -sin(0.5), cos(0.50));
              for (int i = 0; i < NUM_OCTAVES; ++i) {
                v += a * snoise(x);
                x = rot * x * 2.0 + shift;
                a *= 0.5;
              }
              return v;
            }

            void main() {
              vec2 aspectUv = vUv * vec2(uAspect, 1.0);

              float amplitude = sin(uProgress * PI);
              float curve = amplitude * uMaxAmplitude * sin((uHorizontal == 1.0 ? (1.0 - vUv.x) : vUv.y) * PI);

              float rippedNoise1 = fbm(aspectUv * uRippedNoiseFrequency) * uRippedNoiseAmplitude * amplitude;
              float curveNoise1 = snoise((aspectUv + vec2(-0.5)) * uCurveNoiseFrequency) * uCurveNoiseAmplitude * amplitude;

              float rippedNoise2 = fbm((aspectUv + vec2(uRippedDelta)) * uRippedNoiseFrequency) * uRippedNoiseAmplitude * amplitude;
              float curveNoise2 = snoise((aspectUv + vec2(uRippedDelta)) * uCurveNoiseFrequency) * uCurveNoiseAmplitude * amplitude;

              float colorLimit = 1.0 - (uProgress + curve - rippedNoise1 - curveNoise1 - ((uRippedHeight * 0.5) * amplitude));
              float rippedLimit = 1.0 - (uProgress + curve - rippedNoise2 - curveNoise2 + ((uRippedHeight * 0.5) * amplitude));

              gl_FragColor.rgb = uBackground;
              gl_FragColor.a = uBackgroundOpacity;

              if((uHorizontal == 1.0 ? (1.0 - vUv.x) : vUv.y) > colorLimit) {
                // Main body is now the paper texture
                vec4 paperColor = texture2D(uTexture, aspectUv);
                
                // Render centered logo
                vec2 logoUV = (vUv - 0.5) * uLogoScale + 0.5;
                vec4 logoColor = vec4(0.0);
                if (logoUV.x >= 0.0 && logoUV.x <= 1.0 && logoUV.y >= 0.0 && logoUV.y <= 1.0) {
                    // WebGL textures loaded via Image often need Y flipped compared to DOM coordinates
                    logoColor = texture2D(uLogo, vec2(logoUV.x, 1.0 - logoUV.y));
                }
                
                // Blend logo over paper
                gl_FragColor = vec4(mix(paperColor.rgb, logoColor.rgb, logoColor.a), paperColor.a);
              } else if((uHorizontal == 1.0 ? (1.0 - vUv.x) : vUv.y) > rippedLimit) {
                vec4 image = texture2D(uImage, uInverted ? vec2(0.0, 1.0) - vImageUv : vImageUv);
                if(image.a > 0.0) {
                  gl_FragColor = image;
                } else {
                  // Torn edge is now the solid color
                  gl_FragColor = vec4(uColor, 1.0);
                }
              }
            }
        `;

        const compileShader = (type, source) => {
            const shader = gl.createShader(type);
            gl.shaderSource(shader, source);
            gl.compileShader(shader);
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error(gl.getShaderInfoLog(shader));
                gl.deleteShader(shader);
                return null;
            }
            return shader;
        };

        const vertexShader = compileShader(gl.VERTEX_SHADER, vertexShaderSource);
        const fragmentShader = compileShader(gl.FRAGMENT_SHADER, fragmentShaderSource);

        this.program = gl.createProgram();
        gl.attachShader(this.program, vertexShader);
        gl.attachShader(this.program, fragmentShader);
        gl.linkProgram(this.program);
        gl.useProgram(this.program);

        // Quad covering -1 to 1
        const vertices = new Float32Array([
            -1.0, -1.0,  0.0, 0.0,
             1.0, -1.0,  1.0, 0.0,
            -1.0,  1.0,  0.0, 1.0,
             1.0, -1.0,  1.0, 0.0,
             1.0,  1.0,  1.0, 1.0,
            -1.0,  1.0,  0.0, 1.0,
        ]);

        const buffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

        const positionLocation = gl.getAttribLocation(this.program, "position");
        gl.enableVertexAttribArray(positionLocation);
        gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 16, 0);

        const uvLocation = gl.getAttribLocation(this.program, "uv");
        gl.enableVertexAttribArray(uvLocation);
        gl.vertexAttribPointer(uvLocation, 2, gl.FLOAT, false, 16, 8);

        // Set Uniforms
        this.uniformLocations = {
            uHorizontal: gl.getUniformLocation(this.program, "uHorizontal"),
            uProgress: gl.getUniformLocation(this.program, "uProgress"),
            uMaxAmplitude: gl.getUniformLocation(this.program, "uMaxAmplitude"),
            uRippedNoiseFrequency: gl.getUniformLocation(this.program, "uRippedNoiseFrequency"),
            uCurveNoiseFrequency: gl.getUniformLocation(this.program, "uCurveNoiseFrequency"),
            uRippedNoiseAmplitude: gl.getUniformLocation(this.program, "uRippedNoiseAmplitude"),
            uCurveNoiseAmplitude: gl.getUniformLocation(this.program, "uCurveNoiseAmplitude"),
            uAspect: gl.getUniformLocation(this.program, "uAspect"),
            uRippedDelta: gl.getUniformLocation(this.program, "uRippedDelta"),
            uRippedHeight: gl.getUniformLocation(this.program, "uRippedHeight"),
            uColor: gl.getUniformLocation(this.program, "uColor"),
            uBackground: gl.getUniformLocation(this.program, "uBackground"),
            uBackgroundOpacity: gl.getUniformLocation(this.program, "uBackgroundOpacity"),
            uInverted: gl.getUniformLocation(this.program, "uInverted"),
            uRatio: gl.getUniformLocation(this.program, "uRatio"),
            uTexture: gl.getUniformLocation(this.program, "uTexture"),
            uLogo: gl.getUniformLocation(this.program, "uLogo"),
            uLogoScale: gl.getUniformLocation(this.program, "uLogoScale"),
            uImage: gl.getUniformLocation(this.program, "uImage")
        };

        // Default settings for the ripped paper effect
        gl.uniform1f(this.uniformLocations.uHorizontal, 0.0);
        gl.uniform1f(this.uniformLocations.uMaxAmplitude, 0.05);
        gl.uniform1f(this.uniformLocations.uRippedNoiseFrequency, 2.5);
        gl.uniform1f(this.uniformLocations.uCurveNoiseFrequency, 1.5);
        gl.uniform1f(this.uniformLocations.uRippedNoiseAmplitude, 0.05);
        gl.uniform1f(this.uniformLocations.uCurveNoiseAmplitude, 0.03);
        gl.uniform1f(this.uniformLocations.uRippedDelta, 0.5);
        gl.uniform1f(this.uniformLocations.uRippedHeight, 0.1);
        
        // Setup colors (using white)
        gl.uniform3f(this.uniformLocations.uColor, 1.0, 1.0, 1.0);
        gl.uniform3f(this.uniformLocations.uBackground, 0.0, 0.0, 0.0);
        gl.uniform1f(this.uniformLocations.uBackgroundOpacity, 0.0);
        gl.uniform1i(this.uniformLocations.uInverted, 0);

        // Empty dummy texture for uImage so it falls back to uColor
        const dummyTexture = gl.createTexture();
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, dummyTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
        gl.uniform1i(this.uniformLocations.uImage, 1);

        // Load the paper texture
        const paperTexture = gl.createTexture();
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, paperTexture);
        // Put a temporary pixel while it loads
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([200, 200, 200, 255]));
        
        let resolvePaper, resolveLogo;
        const paperReady = new Promise(r => { resolvePaper = r; });
        const logoReady = new Promise(r => { resolveLogo = r; });
        // Resolves once both textures are uploaded (or after a timeout so a
        // failed image never blocks navigation).
        this.texturesReady = Promise.race([
            Promise.all([paperReady, logoReady]),
            new Promise(r => setTimeout(r, 2500))
        ]);

        const image = new Image();
        image.onload = () => {
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, paperTexture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.MIRRORED_REPEAT);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.MIRRORED_REPEAT);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.uniform1i(this.uniformLocations.uTexture, 0);
            this.resize(); // render once loaded
            resolvePaper();
        };
        image.onerror = () => resolvePaper();
        image.crossOrigin = 'anonymous';
        image.src = this.textureUrl;

        // Load the logo texture
        this.logoAspect = 1.0;
        const logoTexture = gl.createTexture();
        gl.activeTexture(gl.TEXTURE2);
        gl.bindTexture(gl.TEXTURE_2D, logoTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
        
        const logoImage = new Image();
        logoImage.onload = () => {
            this.logoAspect = logoImage.width / logoImage.height;
            gl.activeTexture(gl.TEXTURE2);
            gl.bindTexture(gl.TEXTURE_2D, logoTexture);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, logoImage);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.uniform1i(this.uniformLocations.uLogo, 2);
            this.resize();
            resolveLogo();
        };
        logoImage.onerror = () => resolveLogo();
        logoImage.crossOrigin = 'anonymous';
        logoImage.src = this.logoUrl;

        window.addEventListener('resize', () => this.resize());
        this.resize();
    }

    resize() {
        if (!this.gl) return;
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight;
        this.gl.viewport(0, 0, this.canvas.width, this.canvas.height);
        
        let screenAspect = this.canvas.width / this.canvas.height;
        this.gl.uniform1f(this.uniformLocations.uAspect, screenAspect);
        this.gl.uniform2f(this.uniformLocations.uRatio, 1.0, 1.0);
        
        // Full screen "cover" mode for logo
        let imageAspect = this.logoAspect || 1.0;
        let scaleX = 1.0;
        let scaleY = 1.0;
        
        if (screenAspect > imageAspect) {
            scaleY = imageAspect / screenAspect;
        } else {
            scaleX = screenAspect / imageAspect;
        }
        
        this.gl.uniform2f(this.uniformLocations.uLogoScale, scaleX, scaleY);
        
        if (!this.isAnimating) {
            this.render(this.progress);
        }
    }

    render(progressValue) {
        if (!this.gl) return;
        this.gl.clearColor(0, 0, 0, 0);
        this.gl.clear(this.gl.COLOR_BUFFER_BIT);
        this.gl.uniform1f(this.uniformLocations.uProgress, progressValue);
        this.gl.drawArrays(this.gl.TRIANGLES, 0, 6);
    }

    animate(timestamp) {
        if (!this.isAnimating) return;

        if (!this.animationStartTime) this.animationStartTime = timestamp;
        let elapsed = timestamp - this.animationStartTime;
        let p = Math.min(elapsed / this.animationDuration, 1.0);

        // Smooth easing (easeInOutCubic)
        let easeProgress = p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;

        if (this.animationDirection === 1) {
            this.progress = easeProgress;
        } else {
            this.progress = 1.0 - easeProgress;
        }

        this.render(this.progress);

        if (p < 1.0) {
            requestAnimationFrame((t) => this.animate(t));
        } else {
            this.isAnimating = false;
            this.animationStartTime = 0;
            if (this.animationDirection === -1) {
                // Finished entering, remove pointer events
                this.container.classList.remove('is-active');
            } else if (this.resolveCover) {
                // Barba is driving the navigation: hand control back, no reload
                const done = this.resolveCover;
                this.resolveCover = null;
                done();
            } else if (this.pendingUrl) {
                try { sessionStorage.setItem('bonjTransitionActive', '1'); } catch (e) {}
                window.location.href = this.pendingUrl;
            }
            if (this.animationDirection === -1 && this.resolveReveal) {
                const done = this.resolveReveal;
                this.resolveReveal = null;
                done();
            }
        }
    }

    playEnterAnimation() {
        this.container.classList.add('is-active');
        this.progress = 1.0;
        this.render(1.0); // Start fully covered

        // Wait for the textures so the logo is on the paper before it rips open
        (this.texturesReady || Promise.resolve()).then(() => {
            this.render(1.0);
            // Tiny delay to ensure browser has painted the covered state before ripping open
            setTimeout(() => {
                this.animationDirection = -1;
                this.animationStartTime = 0;
                this.isAnimating = true;
                requestAnimationFrame((t) => this.animate(t));
            }, 50);
        });
    }

    playLeaveAnimation(url) {
        this.container.classList.add('is-active');
        this.pendingUrl = url;
        this.progress = 0.0;
        this.animationDirection = 1;
        this.isAnimating = true;
        requestAnimationFrame((t) => this.animate(t));
    }

    // Promise-based API used by pagination-transition.js (Barba.js)
    cover() {
        return (this.texturesReady || Promise.resolve()).then(() => new Promise((resolve) => {
            this.resolveCover = resolve;
            this.container.classList.add('is-active');
            this.pendingUrl = null;
            this.progress = 0.0;
            this.animationDirection = 1;
            this.animationStartTime = 0;
            this.isAnimating = true;
            requestAnimationFrame((t) => this.animate(t));
        }));
    }

    reveal() {
        return new Promise((resolve) => {
            this.resolveReveal = resolve;
            this.playEnterAnimation();
        });
    }

    bindEvents() {
        // When Barba.js handles navigation there is no page reload, so the
        // click-to-reload flow below must stay disabled.
        if (window.barba) return;
        document.addEventListener('click', (e) => {
            const link = e.target.closest('a');
            if (!link) return;
            
            const href = link.getAttribute('href');
            if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
            
            const target = link.getAttribute('target');
            if (target === '_blank') return;
            
            const isInternal = link.hostname === window.location.hostname || !href.startsWith('http');
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
            
            if (isInternal) {
                if (href.split('#')[0] === window.location.pathname) return;
                
                e.preventDefault();
                this.playLeaveAnimation(link.href);
            }
        });
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { window.bonjPageTransition = new WebGLPageTransition(); });
} else {
    window.bonjPageTransition = new WebGLPageTransition();
}
